// Import common configuration
import config from "./config.js";

// Import required modules
import { listen, dom } from "./common/DOM.js";
import Debug from "./common/Debug.js";
import Request from "./common/Request.js";
import User from "./modules/User.js";
import FamilyCarousel from "./modules/FamilyCarousel.js";
import PushNotificationService from "./modules/PushNotificationService.js";
import ServiceWorkerHelper from "./modules/ServiceWorkerHelper.js";

class Main {
  // Private properties
  #user;
  #registeredServiceWorker;
  #serviceWorkerHelper;

  /**
   * Creates an instance of Main.
   * @memberof Main
   */
  constructor() {
    // Create a new instance of our service worker helper class
    this.#serviceWorkerHelper = new ServiceWorkerHelper("/serviceWorker.js");
    if (config.console === true) {
      import("eruda").then((eruda) => eruda.default.init());
    }
  }

  /**
   * @description: The method to initialize the main class including UI, event bindings, and user authentication.
   * @note We now have a #user.isAuthenticated property that combines both Kinde and FamStat authentication states.
   * @memberof Main
   */
  async initialize() {
    this.#displayVersion(config.version);
    try {
      // Fetch any params early to ensure they are available. Don't remove them in case they are needed by the Kinde client.
      // TODO: Move to the constructor
      const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());

      this.#user = new User(config, params);
      await this.#user.init();

      // Configure the UI for authenticated users
      this.#setupUi(this.#user.isAuthenticated);

      // Register the service worker
      this.#registeredServiceWorker = await this.#serviceWorkerHelper.registerServiceWorker(
        `/serviceWorker.js?v=${config.version}`,
        {
          scope: "/",
          type: "module",
        }
      );

      console.log("Service worker status: ", config.version, await this.#serviceWorkerHelper.getStatus());

      if (this.#user.isAuthenticated) {
        // No point in setting up service worker and push notifications if we don't know who the user is
        await this.#handlePushNotifications();
      }
    } catch (err) {
      // Following any error we still want to setup the UI with buttons for Sign In and Sign Up
      this.#setupUi(false);
      // Rethrow so we can catch in our global error handler
      throw new Error(err);
    }
  }

  /**
   * @description The method to setup the UI based on the user's authentication state.
   * @param {boolean} isAuthenticated:  True if the user is already authenticated via Kinde and FamStat
   * @private
   * @memberof Main
   */
  async #setupUi(isAuthenticated) {
    // UI setup common to both states
    const navigation = dom(".header-nav");
    const toggle = dom(".header-toggle");
    const showAuthUi = isAuthenticated || Debug.enabled;

    // Handle on click of the mobile menu toggle
    listen(toggle, "click", () => {
      const active = navigation.dataset.active === "true";
      dom("body").dataset.scroll = active ? "true" : "false";
      navigation.dataset.active = active ? "false" : "true";
      toggle.dataset.active = active ? "false" : "true";
    });

    // Show the signin/signup or checkin/signout buttons based on auth state
    dom(".header")?.setAttribute("data-authenticated", showAuthUi ? "true" : "false");
    dom(".main")?.setAttribute("data-authenticated", showAuthUi ? "true" : "false");

    // Handle the UI based on the auth state
    if (isAuthenticated) {
      // The user is authenticated so we configure the carousel and logout button
      this.carousel = new FamilyCarousel(this.#user);

      // Bind event listeners to the logout button
      listen("#logout", "click", async () => await this.#user.logout());

      // Initialize the carousel functionality
      this.carousel.initialize();
    } else {
      // Bind event listeners to the login button
      listen("#login", "click", async () => await this.#user.login());

      // Bind event listeners to the register button
      listen("#register", "click", async () => await this.#user.register());

      // Handle unauthenticated user redirect or optionally the debug flow
      const location = window.location;
      const path = location.pathname;

      // Optionally initialize the debug flow to troubleshoot specific scenarios
      if (Debug.enabled) {
        this.carousel = new FamilyCarousel(this.#user);
        this.carousel.initialize();
      // Redirect to home, if necessary, while perserving any potential query strings
      } else if (path != "/" && path != "/privacy" && path != "/terms") {
        location.href = "/" + location.search + location.hash;
      }
    }
  }

  /**
   * Handles the service worker and notifications logic.
   */
  async #handlePushNotifications() {
    const pushNotificationService = new PushNotificationService(
      this.#user.data.userId,
      this.#user.token,
      this.#registeredServiceWorker
    );

    // Skip if no service was created
    if (pushNotificationService == null) {
      return;
    }

    // If not on the settings page, skip the rest
    if (!dom(".page-settings")) {
      return;
    }
    // Toggles notifications logic and UI
    if (pushNotificationService.registration.pushManager) {
      this.#togglePushNotifications(pushNotificationService, this.#user);
    }
  }

  /**
   * Toggles notifications logic and UI.
   */
  async #togglePushNotifications(pushService, user) {
    // TODO: Add a message in the UI directing the user to browser's own permissions request in addition to ours (FamStat's)
    // Get the user's subscription from the pushManager
    const subscription = await pushService.registration.pushManager.getSubscription();

    // Get the subscription endpoint. Note this is not the full push provider URL.
    const endpoint = subscription ? await this.#digest(subscription.endpoint) : null;

    // Get a reference to the Enable/Disable Notifications button
    const button = dom(".button-notifications");

    // Determine the initial state of the button based on current subscription state
    let isEnabled = user.data.subscriptions && subscription && user.data.subscriptions.indexOf(endpoint) > -1;
    button.innerHTML = button.dataset[isEnabled ? "disable" : "enable"];
    button.classList.remove("hidden");

    // Toggle the subscription and the UI
    button.addEventListener("click", async () => {
      if (isEnabled) {
        // Disable the subscription
        await Request.delete(`/api/users/${user.data.userId}/push/${endpoint}`, { token: user.token });
        // Remove the subscription from the local copy of the user data.subscriptions so we don't need a network round-trip.
        user.data.subscriptions = user.data.subscriptions.filter((sub) => sub !== endpoint);
        button.innerHTML = button.dataset["enable"];
        isEnabled = false;
      } else {
        // Enable the subscription
        await pushService.getPermission();

        if (pushService.permission === "granted") {
          user.sendMessage(user.data.userId, "Congrats, you have enabled notifications for FamStat.", "Notification Enabled");
          button.innerHTML = button.dataset["disable"];
          isEnabled = true;
        } else {
          console.warn("pushService.permission was not granted.");
        }
      }
    });

    // Send the test notification
    const test = dom(".button-test");
    test.classList.remove("hidden");
    test.onclick = () => user.sendMessage(user.data.userId, "Testing a notification", "Notification Test");
  }

  /**
   * The method to handle displaying the debug version.
   */
  #displayVersion(version) {
    if (document.querySelector(".version")) {
      document.querySelector(".version").innerHTML = version;
    }

    // if (dom(".debug-version")) {
    //   return (dom(".debug-version").innerHTML = version);
    // }

    // const element = document.createElement("div");
    // element.className = "debug-version";
    // element.innerHTML = version;
    // document.body.appendChild(element);
  }

  /**
   * The method to handle long press of settings button to enable debug.
   * TODO: Move to a respective file.
   */
  // eslint-disable-next-line no-unused-private-class-members
  async #handleDebug() {
    const button = dom(".button-settings");

    if (!button) {
      return;
    }

    let timerId;

    const startPress = () => {
      timerId = window.setTimeout(() => {
        window.location.href = "/settings#debug";
      }, 3000);
    };

    const endPress = () => {
      window.clearTimeout(timerId);
    };

    button.addEventListener("mousedown", startPress);
    button.addEventListener("touchstart", startPress);
    button.addEventListener("mouseup", endPress);
    button.addEventListener("touchend", endPress);
    button.addEventListener("mouseleave", endPress);
    button.addEventListener("touchcancel", endPress);
  }

  /**
   * The method to handle hashing.
   */
  async #digest(message, algo = "SHA-1") {
    return Array.from(new Uint8Array(await crypto.subtle.digest(algo, new TextEncoder().encode(message))), (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("");
  }
}

// Wait until the DOM content has loaded before initializing
listen(window, "DOMContentLoaded", () => {
  const main = new Main();
  main.initialize();
});
